import { useLayoutEffect, useState } from "react";
import { useNavigationType } from "react-router";
import { useLocation } from "~/lib/team-router";

const savedScrollPositions = new Map<string, number>();
const restoreTimeoutMs = 5000;

export function useScrollRestoration<T extends HTMLElement>(
  containerName: string
) {
  const [container, setContainer] = useState<T | null>(null);
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const positionKey = `${containerName}:${key}`;

  useLayoutEffect(() => {
    if (!container) return;

    const target =
      navigationType === "POP"
        ? (savedScrollPositions.get(positionKey) ?? 0)
        : 0;
    const canReachTarget = () =>
      container.scrollHeight - container.clientHeight >= target;

    if (canReachTarget()) {
      container.scrollTop = target;
      return;
    }

    container.scrollTop = 0;
    const deadline = performance.now() + restoreTimeoutMs;
    const listeners = new AbortController();
    let frameId = 0;

    const stopRestoring = () => {
      cancelAnimationFrame(frameId);
      listeners.abort();
    };

    const restoreWhenReachable = () => {
      if (canReachTarget()) {
        container.scrollTop = target;
        stopRestoring();
      } else if (performance.now() < deadline) {
        frameId = requestAnimationFrame(restoreWhenReachable);
      } else {
        stopRestoring();
      }
    };

    container.addEventListener(
      "scroll",
      () => {
        if (container.scrollTop !== 0) stopRestoring();
      },
      { passive: true, signal: listeners.signal }
    );
    frameId = requestAnimationFrame(restoreWhenReachable);

    return stopRestoring;
  }, [container, navigationType, positionKey]);

  useLayoutEffect(() => {
    if (!container) return;

    const savePosition = () =>
      savedScrollPositions.set(positionKey, Math.floor(container.scrollTop));
    container.addEventListener("scroll", savePosition, { passive: true });
    return () => container.removeEventListener("scroll", savePosition);
  }, [container, positionKey]);

  return setContainer;
}
